import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { FollowUpCard } from "@/components/FollowUpCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Inbox, AlertTriangle, CalendarClock, CalendarPlus } from "lucide-react";

function List({ view, testid, empty }) {
  const { data = [], isLoading } = useQuery({
    queryKey: ["fu", view],
    queryFn: () => api.get(`/followups?view=${view}`).then((r) => r.data),
  });
  if (isLoading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (data.length === 0) return (
    <div className="rounded-xl border border-dashed border-border p-10 text-center" data-testid={`${testid}-empty`}>
      <Inbox className="h-8 w-8 mx-auto text-muted-foreground/50" />
      <p className="mt-2 text-sm text-muted-foreground">{empty}</p>
    </div>
  );
  return (
    <div className="space-y-3" data-testid={`${testid}-list`}>
      {data.map((fu) => <FollowUpCard key={fu.id} fu={fu} />)}
    </div>
  );
}

export default function FollowUps() {
  const { data: overdue = [] } = useQuery({ queryKey: ["fu", "overdue"], queryFn: () => api.get("/followups?view=overdue").then((r) => r.data) });
  const { data: today = [] } = useQuery({ queryKey: ["fu", "today"], queryFn: () => api.get("/followups?view=today").then((r) => r.data) });

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 fade-in">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Follow-ups</h1>
        <p className="text-sm text-muted-foreground mt-1">Deine geplanten Kontakte im Überblick.</p>
      </div>

      <Tabs defaultValue={overdue.length > 0 ? "overdue" : "today"}>
        <TabsList>
          <TabsTrigger value="overdue" data-testid="tab-overdue" className="gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" /> Überfällig
            {overdue.length > 0 && <span className="ml-1 rounded-full bg-rose-500/20 text-rose-300 px-1.5 text-[11px]">{overdue.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="today" data-testid="tab-today" className="gap-1.5">
            <CalendarClock className="h-3.5 w-3.5" /> Heute
            {today.length > 0 && <span className="ml-1 rounded-full bg-amber-500/20 text-amber-300 px-1.5 text-[11px]">{today.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="upcoming" data-testid="tab-upcoming" className="gap-1.5">
            <CalendarPlus className="h-3.5 w-3.5" /> Kommend
          </TabsTrigger>
        </TabsList>
        <TabsContent value="overdue" className="mt-4"><List view="overdue" testid="overdue" empty="Keine überfälligen Follow-ups." /></TabsContent>
        <TabsContent value="today" className="mt-4"><List view="today" testid="today" empty="Nichts für heute geplant." /></TabsContent>
        <TabsContent value="upcoming" className="mt-4"><List view="upcoming" testid="upcoming" empty="Keine kommenden Follow-ups." /></TabsContent>
      </Tabs>
    </div>
  );
}
